/**
 * Pure feature-flag resolution. SFCC strings and Optimizely values are
 * normalized to the definition type before any comparison. Do not use ==.
 */

const MAX_PAYLOAD_CHARS = 8192

const PREFERENCE_KINDS = {
    boolean: 'boolean',
    enum: 'enum',
    integer: 'integer',
    double: 'double'
}

const kindFor = (definition) => {
    const declared = PREFERENCE_KINDS[definition?.sfcc?.preferenceType]
    if (declared) return declared

    const samples = Object.values(definition?.values || {})
    if (samples.length > 0 && samples.every((value) => typeof value === 'boolean')) return 'boolean'
    if (samples.length > 0 && samples.every((value) => typeof value === 'number' && Number.isInteger(value))) {
        return 'integer'
    }
    if (samples.length > 0 && samples.every((value) => typeof value === 'number')) return 'double'
    return 'enum'
}

const isMissing = (raw) => raw === undefined || raw === null || raw === ''

const normalize = (raw, kind) => {
    if (isMissing(raw)) return {ok: false, missing: true}

    if (kind === 'boolean') {
        if (raw === true || raw === 'true') return {ok: true, value: true}
        if (raw === false || raw === 'false') return {ok: true, value: false}
        return {ok: false}
    }

    if (kind === 'enum') {
        return typeof raw === 'string' ? {ok: true, value: raw} : {ok: false}
    }

    if (kind === 'integer') {
        if (typeof raw === 'number' && Number.isInteger(raw)) return {ok: true, value: raw}
        if (typeof raw === 'string' && /^-?\d+$/.test(raw)) return {ok: true, value: Number(raw)}
        return {ok: false}
    }

    if (kind === 'double') {
        if (typeof raw === 'number' && Number.isFinite(raw)) return {ok: true, value: raw}
        if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
            return {ok: true, value: Number(raw)}
        }
        return {ok: false}
    }

    return {ok: false}
}

const isAllowed = (value, values) => Object.values(values || {}).some((candidate) => candidate === value)

const allowedOrNull = (raw, kind, values) => {
    const normalized = normalize(raw, kind)
    if (!normalized.ok || !isAllowed(normalized.value, values)) return null
    return normalized.value
}

const baselineValue = (definition, sfccValue, kind) => {
    if (definition.sfcc) {
        const live = allowedOrNull(sfccValue, kind, definition.values)
        if (live !== null) return {value: live, source: 'sfcc'}
        const xmlDefault = allowedOrNull(definition.sfcc.defaultValue, kind, definition.values)
        return {value: xmlDefault, source: 'sfcc'}
    }

    return {
        value: allowedOrNull(definition.fallbackValue, kind, definition.values),
        source: 'fallback'
    }
}

const coerceOption = (raw, fallback) => {
    if (typeof fallback === 'boolean') return normalize(raw, 'boolean')
    if (typeof fallback === 'number') {
        return normalize(raw, Number.isInteger(fallback) ? 'integer' : 'double')
    }
    if (typeof fallback === 'string') {
        return typeof raw === 'string' ? {ok: true, value: raw} : {ok: false}
    }
    return {ok: false}
}

const mergeOptions = (defaults, payload, usePayload) => {
    if (!defaults || typeof defaults !== 'object') return {}
    const merged = {}
    Object.keys(defaults).forEach((key) => {
        merged[key] = defaults[key]
        if (!usePayload || !payload || !Object.prototype.hasOwnProperty.call(payload, key)) return
        const coerced = coerceOption(payload[key], defaults[key])
        if (coerced.ok) merged[key] = coerced.value
    })
    return merged
}

const emptyResult = (status = 'unavailable') => ({
    value: null,
    values: {},
    isActive: null,
    matches: () => false,
    options: {},
    status,
    isReady: status !== 'pending',
    source: 'fallback',
    variationKey: '',
    shouldImpress: false
})

/**
 * Resolve one flag.
 * @param {{definition: Object, settings?: Object, sfccValue?: *, decision?: Object, decisionsStatus?: string}} input
 */
const resolveFeatureFlag = ({
    definition,
    settings = {},
    sfccValue,
    decision,
    decisionsStatus = 'idle'
} = {}) => {
    if (!definition || typeof definition !== 'object' || !definition.values || !definition.flagKey) {
        return emptyResult()
    }

    const kind = kindFor(definition)
    const mode = settings.mode === 'experiment' || settings.mode === 'preference'
        ? settings.mode
        : definition.sfcc
            ? 'preference'
            : 'experiment'
    const values = definition.values
    const baseline = baselineValue(definition, sfccValue, kind)

    let value = baseline.value
    let source = baseline.source
    let status = mode === 'preference' ? 'preference' : 'unavailable'
    let variationKey = ''

    // A declared bypassValue is used as written, including undefined, which
    // means Optimizely keeps priority. An omitted key on a boolean flag kills
    // the experiment at false, including a missing runtime value whose default
    // is false. Enums name their kill value.
    const bypassDeclared = !!definition.sfcc &&
        Object.prototype.hasOwnProperty.call(definition.sfcc, 'bypassValue')
    const bypass = bypassDeclared
        ? definition.sfcc.bypassValue
        : definition.sfcc && kind === 'boolean'
            ? false
            : undefined
    const normalizedValue = kind === 'boolean'
        ? (baseline.value === true || baseline.value === false
            ? {ok: true, value: baseline.value}
            : {ok: false})
        : normalize(sfccValue, kind)
    const normalizedBypass = bypass === undefined ? {ok: false} : normalize(bypass, kind)
    const skipped =
        mode === 'experiment' &&
        definition.sfcc &&
        normalizedValue.ok &&
        normalizedBypass.ok &&
        normalizedValue.value === normalizedBypass.value

    if (mode === 'preference' && !definition.sfcc) {
        value = baseline.value
        source = 'fallback'
        status = 'unavailable'
    } else if (skipped) {
        value = normalizedValue.value
        source = 'sfcc'
        status = 'preference'
    } else if (mode === 'experiment' && decisionsStatus === 'pending') {
        status = 'pending'
    } else if (mode === 'experiment' && decisionsStatus === 'ready' && decision?.enabled === true) {
        const experimentValue = allowedOrNull(decision.featureValue, kind, values)
        if (experimentValue !== null) {
            value = experimentValue
            source = 'optimizely'
            status = 'resolved'
            variationKey = typeof decision.variationKey === 'string' ? decision.variationKey : ''
        }
    }

    const options = mergeOptions(settings.options, decision?.featurePayload, source === 'optimizely')
    const isExposed = mode === 'experiment' && settings.isExposed === true

    return {
        value,
        values,
        isActive: kind === 'boolean' ? value === true : null,
        matches: (candidate) => value === candidate,
        options,
        status,
        isReady: status !== 'pending',
        source,
        variationKey,
        shouldImpress: source === 'optimizely' && isExposed
    }
}

const sanitizeFeatureValue = (value) => {
    if (typeof value === 'boolean' || typeof value === 'string') return value
    if (typeof value === 'number' && Number.isFinite(value)) return value
    return undefined
}

const sanitizeFeaturePayload = (value) => {
    let payload = value
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload)
        } catch (error) {
            return {}
        }
    }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {}

    let encoded
    try {
        encoded = JSON.stringify(payload)
    } catch (error) {
        return {}
    }
    return encoded.length > MAX_PAYLOAD_CHARS ? {} : payload
}

/**
 * Keep the CMS decision map intact. Code flags get a separate, whitelisted map.
 * @param {Object} allDecisions getAllDecisions() output
 */
const selectFeatureFlagDecisions = (allDecisions) => {
    if (!allDecisions || typeof allDecisions !== 'object') return {}

    return Object.entries(allDecisions).reduce((acc, [key, decision]) => {
        if (!key.startsWith('fx-')) return acc
        const variables = decision?.variables || {}
        acc[key] = {
            enabled: decision?.enabled === true,
            variationKey: typeof decision?.variationKey === 'string' ? decision.variationKey : '',
            featureValue: sanitizeFeatureValue(variables.feature_value),
            featurePayload: sanitizeFeaturePayload(variables.feature_payload)
        }
        return acc
    }, {})
}

export {resolveFeatureFlag, selectFeatureFlagDecisions, normalize, MAX_PAYLOAD_CHARS}
